import { Injectable } from '@angular/core';
import {
  AdvancedExportRow,
  WinesuitePaymentsListQuery,
  WinesuitePaymentsPage,
  WSPaymentsService,
} from '@services/dws/ws-payments.service';
import { tr } from '@util/tr';
import { observeNoLoading } from '@web/util/loading/loading';
import { toast } from '@web/util/toast';
import { WsDownloadsService } from '@web/app/services/ws-downloads.service';
import { Observable, Subject, Subscription, of, BehaviorSubject, from } from 'rxjs';
import { concatMap, map, mergeMap, takeUntil, toArray } from 'rxjs/operators';
import { PaymentsAdvancedExport } from './payments-advanced-export';
import {
  buildListExportFile,
  EXPORT_PAGE_SIZE,
  mapSummaryToExportRow,
  PaymentsListExportFormat,
} from './payments-list-export';

export const PAYMENTS_LIST_EXPORT_JOB_ID = 'payments-list-export';

/** Parallel list pages during export. */
const LIST_EXPORT_PAGE_CONCURRENCY = 4;

export interface PaymentsListExportRequest {
  wineryId: string;
  query: WinesuitePaymentsListQuery;
  format: PaymentsListExportFormat;
}

interface ListExportProgress {
  current: number;
  total: number;
}

@Injectable({ providedIn: 'root' })
export class PaymentsListExportService {
  readonly running$ = new BehaviorSubject<boolean>(false);
  private runSubscription?: Subscription;
  private cancel$ = new Subject<void>();
  private cancelled = false;
  private pagesCompleted = 0;

  constructor(
    private readonly wsPaymentsService: WSPaymentsService,
    private readonly downloads: WsDownloadsService,
  ) {}

  get isRunning(): boolean {
    return this.running$.value;
  }

  startExport(request: PaymentsListExportRequest): void {
    if (this.runSubscription && !this.runSubscription.closed) {
      return;
    }
    if (this.isRunning) {
      this.finishRunning();
      this.clearRunningDownload();
    }

    this.runSubscription?.unsubscribe();
    this.cancel$ = new Subject<void>();
    this.cancelled = false;
    this.pagesCompleted = 0;
    this.setRunning({ current: 0, total: 1 });
    toast(tr('payments.list.exportStarted'));

    this.runSubscription = this.fetchAllPages(request).pipe(
      concatMap((rows) => this.enrichPayoutColumns(request.wineryId, request.query, rows)),
      takeUntil(this.cancel$),
    ).subscribe({
      next: (rows) => {
        if (this.cancelled) {
          return;
        }
        this.finalizeExport(request.format, rows);
      },
      error: (err) => {
        if (this.cancelled) {
          return;
        }
        console.error('[payments.list.export] Export failed:', err);
        this.finishRunning();
        this.clearRunningDownload();
        toast(tr('payments.list.exportError'));
      },
      complete: () => {
        if (this.cancelled) {
          return;
        }
        if (this.isRunning) {
          this.finishRunning();
          this.clearRunningDownload();
        }
      },
    });
  }

  cancelExport(): void {
    if (!this.isRunning) {
      return;
    }

    this.cancelled = true;
    this.cancel$.next();
    this.cancel$.complete();
    this.runSubscription?.unsubscribe();
    this.runSubscription = undefined;
    this.finishRunning();
    this.clearRunningDownload();
    toast(tr('payments.list.exportCancelled'));
  }

  private fetchAllPages(request: PaymentsListExportRequest): Observable<AdvancedExportRow[]> {
    return observeNoLoading(
      this.wsPaymentsService.getWinesuitePayments(
        request.wineryId,
        0,
        EXPORT_PAGE_SIZE,
        request.query,
      ),
    ).pipe(
      concatMap((firstPage) => {
        const totalElements = firstPage?.totalElements ?? 0;
        const totalPages = Math.max(1, Math.ceil(totalElements / EXPORT_PAGE_SIZE));
        const firstRows = this.rowsFromPaymentsPage(firstPage);
        this.pagesCompleted = 1;
        this.setRunning({ current: this.pagesCompleted, total: totalPages });

        if (totalPages <= 1) {
          return of(firstRows);
        }

        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 1);
        return from(remainingPages).pipe(
          mergeMap(
            (page) => observeNoLoading(
              this.wsPaymentsService.getWinesuitePayments(
                request.wineryId,
                page,
                EXPORT_PAGE_SIZE,
                request.query,
              ),
            ).pipe(
              map((pageResult) => {
                this.pagesCompleted += 1;
                this.setRunning({ current: this.pagesCompleted, total: totalPages });
                return this.rowsFromPaymentsPage(pageResult);
              }),
            ),
            LIST_EXPORT_PAGE_CONCURRENCY,
          ),
          toArray(),
          map((rest) => firstRows.concat(...rest)),
        );
      }),
    );
  }

  private rowsFromPaymentsPage(page: WinesuitePaymentsPage | null | undefined): AdvancedExportRow[] {
    const payments = page?.payments ?? [];
    const rows: AdvancedExportRow[] = [];
    for (const summary of payments) {
      const row = mapSummaryToExportRow(summary);
      if (row) {
        rows.push(row);
      }
    }
    return rows;
  }

  private enrichPayoutColumns(
    wineryId: string,
    query: PaymentsListExportRequest['query'],
    rows: AdvancedExportRow[],
  ): Observable<AdvancedExportRow[]> {
    if (!rows.length) {
      return of(rows);
    }

    const { dateFrom, dateToExclusive } = PaymentsAdvancedExport.computeListExportPayoutLookupRange(rows, query);
    const chunks = PaymentsAdvancedExport.buildExportChunks(
      dateFrom,
      dateToExclusive,
      PaymentsAdvancedExport.ADVANCED_EXPORT_MAX_CHUNK_DAYS,
    );
    if (!chunks.length) {
      return of(rows);
    }

    let chunkIndex = 0;
    return from(chunks).pipe(
      mergeMap(
        (chunk) => {
          chunkIndex += 1;
          this.setRunning({ current: chunkIndex, total: chunks.length });
          return observeNoLoading(
            this.wsPaymentsService.queryAdvancedExport({
              wineryId,
              dateFrom: chunk.dateFrom,
              dateTo: chunk.dateTo,
              mode: 'PAYMENTS',
              exportScope: 'payments',
              includePayout: true,
            }),
          ).pipe(map((response) => response?.rows ?? []));
        },
        PaymentsAdvancedExport.ADVANCED_EXPORT_CHUNK_CONCURRENCY,
      ),
      toArray(),
      map((chunkResults) => {
        const advancedRows = chunkResults.reduce(
          (acc, chunk) => acc.concat(chunk),
          [] as AdvancedExportRow[],
        );
        PaymentsAdvancedExport.mergePayoutEnrichmentIntoRows(rows, advancedRows);
        return rows;
      }),
    );
  }

  private finalizeExport(format: PaymentsListExportFormat, rows: AdvancedExportRow[]): void {
    this.finishRunning();

    if (!rows.length) {
      this.clearRunningDownload();
      toast(tr('payments.list.exportNoData'));
      return;
    }

    const built = buildListExportFile(rows, format === 'xlsx' ? 'xlsx' : 'csv');
    if (!built) {
      this.clearRunningDownload();
      toast(tr('payments.list.exportNoData'));
      return;
    }

    this.downloads.completeDownload(PAYMENTS_LIST_EXPORT_JOB_ID, {
      id: PAYMENTS_LIST_EXPORT_JOB_ID,
      label: tr('payments.list.exportReady'),
      hint: built.filename,
      blob: built.blob,
      filename: built.filename,
    });
    this.downloads.triggerDownload(PAYMENTS_LIST_EXPORT_JOB_ID);
    toast(tr('payments.list.exportComplete'));
  }

  private setRunning(progress: ListExportProgress): void {
    this.running$.next(true);
    this.downloads.upsertDownload({
      id: PAYMENTS_LIST_EXPORT_JOB_ID,
      label: tr('payments.advancedExport.headerStatus'),
      hint: tr('payments.advancedExport.background'),
      progress,
      status: 'running',
      cancel: () => this.cancelExport(),
    });
  }

  private finishRunning(): void {
    this.running$.next(false);
    this.runSubscription = undefined;
  }

  private clearRunningDownload(): void {
    const existing = this.downloads.downloads$.value.find((entry) => entry.id === PAYMENTS_LIST_EXPORT_JOB_ID);
    if (existing?.status === 'running') {
      this.downloads.removeDownload(PAYMENTS_LIST_EXPORT_JOB_ID);
    }
  }
}
